/**
 * Service layer — Phases domain
 *
 * Owns: Phases (tblNl3JpfIEWk9tPX)
 */

import { apiGet, apiPost, apiPatch } from './_api.js'
import { enrichPhaseRecords } from './phaseFinancials.js'

const TBL_PH = 'tblNl3JpfIEWk9tPX'

/** All phases for a project (full records — no fields[], so formula/rollup budget fields stay intact),
 *  sorted by Sort Order. Mirrors the outline engine's phase load. For the phase-landscape view.
 *  Fee/Hours Expended (and everything derived from them) are corrected against live Supabase time
 *  entries before returning — see services/phaseFinancials.js. */
export function getProjectPhases(projectId) {
  if (!projectId) return Promise.resolve([])
  const qs = 'filterByFormula=' + encodeURIComponent('FIND("' + projectId + '",ARRAYJOIN({Project Record UID (from Project)},","))')
    + '&sort%5B0%5D%5Bfield%5D=Sort+Order&sort%5B0%5D%5Bdirection%5D=asc&maxRecords=40'
  return apiGet(TBL_PH, qs).then(function (records) { return enrichPhaseRecords(records || []) })
}

/**
 * Resolve a shorthand phase string (e.g. Meetings.Phase — "Schematic Design", a plain singleSelect,
 * not a link to this table) to the project's ACTUAL Phase Name ("Schematic Design Phase"). Every
 * meeting-Phase option is a strict prefix of some project's real Phase Name, so a case-insensitive
 * prefix match is reliable; falls back to the raw value if nothing matches (never silently drops the
 * phase). `phaseNames` is a plain array of Phase Name strings (e.g. from getProjectPhases mapped to
 * `.fields['Phase Name']`, or the JourneyEntryModal's already-flattened phase-picker options).
 */
export function matchPhaseName(rawPhase, phaseNames) {
  if (!rawPhase) return rawPhase
  const hit = (phaseNames || []).find(name => (name || '').toLowerCase().startsWith(rawPhase.toLowerCase()))
  return hit || rawPhase
}

// Clerk-authed POST to a server endpoint (no browser secret) — for BQE-writing routes.
async function authedPost(path, body) {
  const token = window.__clerkGetToken ? await window.__clerkGetToken() : null
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify(body || {}),
  })
  let data = {}; try { data = await res.json() } catch (e) { data = {} }
  if (!res.ok && data && !data.error) data.error = 'Request failed: ' + res.status
  return data
}

/**
 * Add a phase to a project (server-side, api/add-phase). Pass `bqeCoreId` to LINK an existing BQE
 * Core phase; omit it to have Cortex CREATE the phase in Core first. The server writes one Airtable
 * Phase row (Manual Add + the type-specific fields). `phaseType`: 'basic' | 'additional' | 'travel'.
 * @returns {Promise<{id?, bqeId?, error?}>}
 */
export function addPhase(params) {
  return authedPost('/api/add-phase', params || {})
}

/**
 * Create a Phase under a project, linked to an EXISTING BQE Core phase (the caller supplies its
 * Core record id — this does NOT create anything in Core, unlike api/scaffold-phase.js).
 * @param {object} p
 * @param {string} p.projectId      — Airtable Projects record id (the link)
 * @param {string} p.phaseName       — phase display name
 * @param {string} p.bqeCoreId       — existing BQE Core phase record id
 * @param {string} [p.contractType]  — 'Fixed Fee' | 'Hourly'
 * @param {number} [p.contractedFee] — fee for Fixed Fee phases
 * @param {number} [p.sortOrder]     — position within the project
 * @returns {Promise<{id,fields}>} the created phase record
 */
export function createPhase({ projectId, phaseName, bqeCoreId, contractType, contractedFee, sortOrder }) {
  const fields = {
    'Phase Name': phaseName,
    'Project': [projectId],
    'BQE Core Record ID': bqeCoreId,
  }
  if (contractType) fields['Contract Type'] = contractType
  if (contractedFee != null && contractedFee !== '') fields['Contracted Fee'] = Number(contractedFee)
  // NOTE: 'Sort Order' is a COMPUTED field in Airtable — never write it (Airtable rejects the PATCH).
  return apiPost(TBL_PH, fields)
}

/**
 * Create a phase by making it in BQE Core FIRST (under the project's Core engagement) and linking
 * the new Core id back into Cortex — the "start in Cortex" direction. Server-side (api/bqe-create-phase).
 * @param {object} p
 * @param {string} p.projectId     — Airtable Projects record id
 * @param {string} p.phaseName
 * @param {string} [p.contractType] — 'Fixed Fee' | 'Hourly'
 * @param {number} [p.fee]          — contracted fee (required > 0 for Fixed Fee)
 * @param {string} [p.code]         — optional Core phase code (auto-derived if omitted)
 * @returns {Promise<{id?, bqeId?, error?}>}
 */
export function createPhaseInCore({ projectId, phaseName, contractType, fee, code }) {
  return authedPost('/api/bqe-create-phase', { projectRecordId: projectId, phaseName, contractType, fee, code })
}

/**
 * Toggle time entry permission on a phase.
 * @param {string} phaseId — Airtable phase record ID
 * @param {boolean} allow — new value for Allow Time Entries
 * @returns {Promise} Raw Airtable PATCH response
 */
// Anything that opens or closes a phase for time entry tells the open time card, so its phase lists
// refresh without reloading the page (Matt, 2026-10-22: unlocked phases didn't show until a refresh).
function phaseTimeChanged(r) {
  try { window.dispatchEvent(new CustomEvent('phase-time-entry-changed')) } catch { /* not in a browser */ }
  return r
}

export function updatePhaseTimeEntries(phaseId, allow) {
  return apiPatch(TBL_PH, phaseId, { 'Allow Time Entries': allow }).then(phaseTimeChanged)
}

export function closeOutPhase(phaseId) {
  return apiPatch(TBL_PH, phaseId, { 'Phase Closed Out': true, 'Allow Time Entries': false }).then(phaseTimeChanged)
}

/**
 * Record the one-question close-out post-mortem (api/phase-closeout-review.js) — captured by
 * phaseLandscape.js#doClose when a phase being closed is currently Over Budget. category is
 * 'scope' | 'pricing' | 'pace' | 'other' | null; note is optional free text. See
 * docs/EBR_FRAMEWORK.md's "Phase budget threshold notifications" section.
 */
export function saveCloseoutReview(phaseId, category, note) {
  return authedPost('/api/phase-closeout-review', { action: 'save', phaseAirtableId: phaseId, category, note })
}

export function reopenPhase(phaseId, allowTime = false) {
  const fields = { 'Phase Closed Out': false }
  if (allowTime) fields['Allow Time Entries'] = true
  return apiPatch(TBL_PH, phaseId, fields).then(phaseTimeChanged)
}

export function updatePhaseCustomTitle(phaseId, title) {
  return apiPatch(TBL_PH, phaseId, { 'Custom Title': title })
}

/**
 * Save a phase's manual Percent Complete Override (+ Last Update By). The single write shape shared
 * by the Billing Run's phase modal (`billing/runTool.js#savePhasePercent`) and the Progress Gate
 * (`components/ProgressGate.jsx`) — one place defines how a phase % actually gets saved. Percent
 * Complete Updated is Airtable's own lastModifiedTime formula watching the Override field, so it
 * updates itself; nothing to write here for that.
 * @param {string} phaseId
 * @param {number} pctDecimal — 0–1
 * @param {string} [memberId] — Airtable Members record id
 */
export function savePhasePercentComplete(phaseId, pctDecimal, memberId) {
  const fields = { 'Percent Complete Override': pctDecimal }
  if (memberId) fields['Last Update By'] = [memberId]
  return apiPatch(TBL_PH, phaseId, fields)
}
